import { ArrowLeft, MapPin, MessageCircle, Globe, Clock, Briefcase } from 'lucide-react';
import { useRouter } from '../router/MarketplaceRouter';
import {
  getVendorById,
  getServicesByVendor,
  getCategoryById,
} from '../data/fixtures';
import {
  Button,
  PageSection,
  RatingLine,
  VerifiedBadge,
  ServiceCard,
  Avatar,
} from '../shell/ui';

export function VendorProfileScreen() {
  const { route, navigate, back } = useRouter();
  const vendor = getVendorById(route.params?.vendorId || '');
  const services = vendor ? getServicesByVendor(vendor.id) : [];

  if (!vendor) {
    return (
      <PageSection>
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <h1>Vendor not found</h1>
          <Button onClick={() => navigate('browse')}>Browse categories</Button>
        </div>
      </PageSection>
    );
  }

  return (
    <>
      {/* Cover band */}
      <section
        style={{
          position: 'relative',
          height: '340px',
          background: `linear-gradient(180deg, rgba(28, 27, 26, 0) 30%, rgba(28, 27, 26, 0.72) 100%), url(${vendor.cover}) center/cover no-repeat`,
          borderBottom: '1px solid var(--color-border-light)',
        }}
      >
        <div
          style={{
            maxWidth: '1240px',
            margin: '0 auto',
            padding: '1.75rem 1.5rem',
          }}
        >
          <button
            onClick={back}
            style={{
              all: 'unset',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.875rem',
              background: 'rgba(255,255,255,0.92)',
              color: 'var(--color-text-primary)',
              border: '1px solid rgba(255,255,255,0.6)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.8125rem',
              fontWeight: 500,
              backdropFilter: 'blur(10px)',
            }}
          >
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </section>

      {/* Identity band (overlaps cover) */}
      <PageSection paddingY="0">
        <div
          style={{
            marginTop: '-4.5rem',
            background: 'var(--color-background-card)',
            border: '1px solid var(--color-border-light)',
            borderRadius: 'var(--radius-xl)',
            padding: '2rem 2.25rem',
            boxShadow: 'var(--shadow-medium)',
            position: 'relative',
            zIndex: 2,
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            gap: '1.75rem',
            alignItems: 'center',
          }}
          className="vendor-id-band"
        >
          <Avatar src={vendor.avatar} size={92} />

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '0.375rem',
                flexWrap: 'wrap',
              }}
            >
              <h1
                style={{
                  fontFamily: 'var(--font-family-display)',
                  fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
                  fontWeight: 400,
                  lineHeight: 1.1,
                  letterSpacing: '-0.015em',
                  margin: 0,
                }}
              >
                {vendor.name}
              </h1>
              {vendor.verified && <VerifiedBadge />}
            </div>
            <p
              style={{
                fontFamily: 'var(--font-family-body)',
                fontSize: '1.0625rem',
                lineHeight: 1.5,
                color: 'var(--color-text-secondary)',
                margin: 0,
                marginBottom: '0.75rem',
              }}
            >
              {vendor.headline}
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.25rem',
                fontSize: '0.875rem',
                color: 'var(--color-text-muted)',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                <MapPin size={13} /> {vendor.location}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                <Briefcase size={13} /> Since {vendor.yearFounded}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                <Clock size={13} /> Replies {vendor.responseTime}
              </span>
              <RatingLine rating={vendor.rating} reviewCount={vendor.reviewCount} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.625rem' }}>
            <Button variant="secondary" onClick={() => navigate('messages')} leftIcon={<MessageCircle size={15} />}>
              Message
            </Button>
            <Button onClick={() => navigate('request')}>Request a quote</Button>
          </div>
        </div>

        <style>{`
          @media (max-width: 860px) {
            .vendor-id-band {
              grid-template-columns: auto 1fr !important;
            }
            .vendor-id-band > div:last-child {
              grid-column: 1 / -1;
              display: flex;
              width: 100%;
            }
            .vendor-id-band > div:last-child > button {
              flex: 1;
            }
          }
        `}</style>
      </PageSection>

      {/* Body: bio + stats + categories */}
      <PageSection paddingY="3.5rem">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.6fr 1fr',
            gap: '2.5rem',
            alignItems: 'start',
          }}
          className="vendor-body"
        >
          {/* About */}
          <div
            style={{
              background: 'var(--color-background-card)',
              border: '1px solid var(--color-border-light)',
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: '1.5rem',
                fontWeight: 400,
                marginBottom: '1rem',
                letterSpacing: '-0.005em',
              }}
            >
              About the studio
            </div>
            <p
              style={{
                fontSize: '1.0625rem',
                lineHeight: 1.65,
                color: 'var(--color-text-secondary)',
                margin: 0,
              }}
            >
              {vendor.bio}
            </p>

            <div
              style={{
                display: 'flex',
                gap: '1rem',
                marginTop: '2rem',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                <Globe size={14} /> {vendor.languages.join(' · ')}
              </div>
            </div>
          </div>

          {/* Stats panel */}
          <div
            style={{
              background: 'var(--color-background-card)',
              border: '1px solid var(--color-border-light)',
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
            }}
          >
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                marginBottom: '1rem',
              }}
            >
              At a glance
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {[
                ['Completed projects', `${vendor.completedProjects}`],
                ['Average rating', `${vendor.rating.toFixed(1)} / 5`],
                ['Reviews', `${vendor.reviewCount}`],
                ['Founded', `${vendor.yearFounded}`],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    paddingBottom: '0.75rem',
                    borderBottom: '1px solid var(--color-border-light)',
                  }}
                >
                  <span style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>{label}</span>
                  <span
                    style={{
                      fontFamily: 'var(--font-family-display)',
                      fontSize: '1.25rem',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: '1.5rem',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
                marginBottom: '0.75rem',
              }}
            >
              Specialties
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {vendor.categories.map((cId) => {
                const c = getCategoryById(cId);
                if (!c) return null;
                return (
                  <button
                    key={cId}
                    onClick={() => navigate('category', { categoryId: cId })}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      padding: '0.375rem 0.75rem',
                      background: 'var(--color-background-surface)',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '999px',
                      fontSize: '0.8125rem',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            .vendor-body { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </PageSection>

      {/* Portfolio masonry */}
      <PageSection paddingY="3rem" background="var(--color-background-surface)">
        <div
          style={{
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: 'var(--color-accent-rosegold)',
            fontWeight: 500,
            marginBottom: '0.75rem',
          }}
        >
          Portfolio
        </div>
        <h2
          style={{
            fontFamily: 'var(--font-family-display)',
            fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
            fontWeight: 400,
            lineHeight: 1.15,
            letterSpacing: '-0.015em',
            margin: 0,
            marginBottom: '2.5rem',
          }}
        >
          Selected work.
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem',
          }}
          className="vendor-portfolio"
        >
          {vendor.portfolio.map((p, i) => {
            const tall = i % 5 === 0 || i % 5 === 3;
            return (
              <div
                key={p.id}
                style={{
                  position: 'relative',
                  aspectRatio: tall ? '3/4' : '4/3',
                  borderRadius: 'var(--radius-lg)',
                  overflow: 'hidden',
                  background: `url(${p.image}) center/cover no-repeat`,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(28, 27, 26, 0) 50%, rgba(28, 27, 26, 0.72) 100%)',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    padding: '1rem 1.125rem 0.875rem',
                    color: 'var(--color-text-inverse)',
                    fontFamily: 'var(--font-family-display)',
                    fontStyle: 'italic',
                    fontSize: '0.9375rem',
                  }}
                >
                  {p.title}
                </div>
              </div>
            );
          })}
        </div>
        <style>{`
          @media (max-width: 900px) {
            .vendor-portfolio { grid-template-columns: repeat(2, 1fr) !important; }
          }
          @media (max-width: 560px) {
            .vendor-portfolio { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </PageSection>

      {/* Services from this vendor */}
      {services.length > 0 && (
        <PageSection>
          <div
            style={{
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--color-accent-rosegold)',
              fontWeight: 500,
              marginBottom: '0.75rem',
            }}
          >
            Services
          </div>
          <h2
            style={{
              fontFamily: 'var(--font-family-display)',
              fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              fontWeight: 400,
              lineHeight: 1.15,
              letterSpacing: '-0.015em',
              margin: 0,
              marginBottom: '2.5rem',
            }}
          >
            What you can book.
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.25rem',
            }}
            className="vendor-services-grid"
          >
            {services.map((s) => (
              <ServiceCard
                key={s.id}
                image={s.heroImage}
                title={s.title}
                vendorName={vendor.name}
                vendorAvatar={vendor.avatar}
                rating={s.rating}
                reviewCount={s.reviewCount}
                priceFrom={s.priceFrom}
                onClick={() => navigate('service', { serviceId: s.id })}
              />
            ))}
          </div>
          <style>{`
            @media (max-width: 980px) { .vendor-services-grid { grid-template-columns: repeat(2, 1fr) !important; } }
            @media (max-width: 620px) { .vendor-services-grid { grid-template-columns: 1fr !important; } }
          `}</style>
        </PageSection>
      )}
    </>
  );
}
